'use client';

import {
  QueryClient,
  type QueryClientConfig,
  QueryClientProvider,
  isServer,
} from '@tanstack/react-query';
import type { ReactNode } from 'react';

/**
 * Query client configuration.
 *
 * @see https://tanstack.com/query/latest/docs/reference/QueryClient
 */

const config: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // With SSR, we usually want to set some default staleTime
      // above 0 to avoid refetching immediately on the client
      staleTime: 60 * 1000,
    },
  },
};

// Returns a new query client instance.

const makeQueryClient = () => new QueryClient(config);

// Variable that act as a singleton for the query client in the browser.

let browserQueryClient: QueryClient | undefined = undefined;

/**
 * Returns the query client instance.
 *
 * On the server, always make a new query client.
 *
 * On the browser, make a new query client if we don't already have one.
 * This is very important, so we don't re-make a new client if React suspends during the initial render.
 * This may not be needed if we have a suspense boundary BELOW the creation of the query client.
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/advanced-ssr#server-components--nextjs-app-router
 */

const getQueryClient = () => {
  if (isServer) {
    return makeQueryClient();
  } else {
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient();
    }

    return browserQueryClient;
  }
};

// Type definition of the ReactQueryProvider properties.

type Props = {
  children: ReactNode;
};

/**
 * ReactQueryProvider component.
 *
 * Initializes `@tanstack/react-query` for the App router.
 *
 * IMPORTANT:
 *
 * Avoid `useState` when initializing the query client if you don't
 * have a suspense boundary between this and the code that may
 * suspend because React will throw away the client on the initial
 * render if it suspends and there is no boundary.
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/advanced-ssr#server-components--nextjs-app-router
 */

const QueryProvider = ({ children }: Props) => {
  const queryClient = getQueryClient();

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

export default QueryProvider;
